import classNames from 'clsx';
import React, { type ReactElement, memo, useCallback } from 'react';
import { shouldShowDownloadBlockInExp } from 'reactApp/modules/redirectToApp/redirectToApp.helpers';
import type { EStorageType } from 'reactApp/modules/storage/storage.types';
import type { IOptionItem } from 'reactApp/ui/Mobile/OptionsModal/OptionsModal.types';
import { Icon24Check } from 'reactApp/ui/touchIcons';
import { noopVoid } from 'reactApp/utils/helpers';

import styles from './OptionsBlock.css';

interface IProps {
    header?: string;
    id: string;
    items: IOptionItem[];
    onClose?: () => void;
    headerComponent?: ReactElement;
    rightItem?: ReactElement;
    storage?: EStorageType;
}

/**
 * Определяет, нужно ли закрывать модальное окно после клика на опцию.
 * Логика:
 * - 'more' — никогда не закрываем.
 * - 'upload' — закрываем только если активен эксперимент auth-mobile-redirect-app-main-actions
 *   и редирект ещё не был показан.
 * - Все остальные действия — закрываем.
 */
const shouldCloseModal = (id: string, storage?: EStorageType): boolean => {
    // 'more' — не закрываем
    if (id === 'more') {
        return false;
    }

    // 'upload' — проверяем условия эксперимента
    if (id === 'upload') {
        /**
         * @expFlag auth-mobile-redirect-app-main-actions
         * @ticket CLOUDWEB-20237
         * @ifLose удалить условие
         */
        return shouldShowDownloadBlockInExp({ storage });
    }

    // Все остальные действия — закрываем
    return true;
};

export const OptionsBlock = memo<IProps>(({ id, header, items, headerComponent, onClose = noopVoid, storage }): ReactElement => {
    const onItemClick = useCallback(
        (onClick = noopVoid, id: string) => {
            onClick();
            if (shouldCloseModal(id, storage)) {
                onClose();
            }
        },
        [onClose, storage]
    );

    return (
        <div
            className={classNames({
                [styles.root]: true,
                [styles[`root_${id}`]]: !!id,
            })}
            data-qa-options={id}
        >
            {header && (
                <div className={styles.header}>
                    {header}
                    {headerComponent}
                </div>
            )}
            {items.map((item) => (
                <div
                    key={item.id}
                    className={classNames(styles.item, { [styles.item_disabled]: item?.disabled })}
                    onClick={() => onItemClick(item.onClick, item.id)}
                    data-qa-option={item.id}
                >
                    {item.component}
                    {item.icon && <div className={styles.icon}>{item.icon}</div>}
                    <div className={styles.text}>{item.text}</div>
                    {item.rightItem && <div className={styles.rightItem}>{item.rightItem}</div>}
                    {item?.active && (
                        <div className={styles.check}>
                            <Icon24Check height={24} width={24} fill="currentColor" />
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
});

OptionsBlock.displayName = 'OptionsBlock';
